import {
  ExchangeRate,
  Loadable,
  LoadableState,
  OrderExchangeData,
  OrderExchangeFundingData,
  OrderExchangeOrderFields,
  getDisplayExchangeRate,
} from '@eih-netbroker/common';
import { useCallback, useMemo } from 'react';
import { useLocale } from '../../../../../context/NbLocalizationContext';
import { NbIndefiniteLoaderValueWrapper } from '../../../../../shared/components/loader-value/NbIndefiniteLoaderValueWrapper';
import { MoneyMaskHOC } from '../../../../../shared/components/money-mask/MoneyMask';
import { useFormatter } from '../../../../../shared/services/utils/useFormatter';
import { cssWhen } from '../../../../../shared/utils/themeUtils';
import { OrderFormSourceComponent } from '../../../types/orderTypes';
import classes from './OrderCurrencyExchangeSummary.module.scss';

type FundingWithOrderFields = {
  funding: OrderExchangeFundingData;
  orderFields: OrderExchangeOrderFields;
  exchangeRate: ExchangeRate;
};

type OrderCurrencyExchangeSummaryProps = OrderExchangeData & { sourceComponent: OrderFormSourceComponent };

export const OrderCurrencyExchangeSummary = ({
  isEdit,
  funding,
  fundingMode,
  orderFields,
  exchangeRate,
  sourceComponent,
}: OrderCurrencyExchangeSummaryProps) => {
  const { translation } = useLocale();
  const t = translation.orderModal.currencyExchangeForm;
  const { formatCurrency, formatCurrencyMaxF5 } = useFormatter();

  const fundingWithOrderFields = useMemo<LoadableState<FundingWithOrderFields>>(
    () =>
      Loadable.merge(
        [funding, orderFields, exchangeRate],
        (funding, orderFields, exchangeRate): FundingWithOrderFields => ({ funding, orderFields, exchangeRate }),
      ),
    [funding, orderFields, exchangeRate],
  );

  const getOrderValue = useCallback(
    (data: OrderExchangeFundingData): string =>
      formatCurrency(isEdit ? data.remaining.total : data.remaining.orderValue, data.targetCurrency),
    [formatCurrency, isEdit],
  );

  const getCommission = useCallback(
    (data: OrderExchangeFundingData): string => formatCurrency(data.remaining.commission, data.targetCurrency),
    [formatCurrency],
  );

  const getAvailableBalance = useCallback(
    (data: OrderExchangeFundingData): string => formatCurrency(data.balanceInTargetCurrency, data.targetCurrency),
    [formatCurrency],
  );

  const getMissingValue = useCallback(
    (data: OrderExchangeFundingData): string => formatCurrency(data.missingAmount, data.targetCurrency),
    [formatCurrency],
  );

  const getExchangeAmount = useCallback(
    (data: FundingWithOrderFields): string =>
      formatCurrency(data.orderFields.currencySourceAmount, data.orderFields.currencySourceCurrency),
    [formatCurrency],
  );

  const getExchangeRateLabel = useCallback(
    (data: FundingWithOrderFields): string => {
      const { dominantCurrency, secondaryCurrency, exchangeRate } = getDisplayExchangeRate(data.exchangeRate);
      return `1 ${dominantCurrency} = ${formatCurrencyMaxF5(exchangeRate, secondaryCurrency)}`;
    },
    [formatCurrencyMaxF5],
  );

  return (
    <div className={classes.container}>
      <div className={cssWhen(classes.section, [fundingMode === 'fullAmount', classes.singleColumnSection])}>
        <div className={classes.column}>
          <div className={classes.row}>
            <span
              data-testid={`order-currency-exchange-summary-${isEdit ? 'difference' : 'order'}-value-label`}
              className={classes.label}
            >
              {isEdit ? t.differenceValue : t.orderValue}
            </span>
            <span
              data-testid={`order-currency-exchange-summary-${isEdit ? 'difference' : 'order'}-value`}
              className={classes.value}
            >
              <NbIndefiniteLoaderValueWrapper
                loadableValue={funding}
                loadedValueCb={data => getOrderValue(data)}
              />
            </span>
          </div>
          <div className={classes.row}>
            <span
              data-testid="order-currency-exchange-summary-commission-label"
              className={classes.label}
            >
              {t.commission}
            </span>
            <span
              data-testid="order-currency-exchange-summary-commission-value"
              className={classes.value}
            >
              <NbIndefiniteLoaderValueWrapper
                loadableValue={funding}
                loadedValueCb={data => getCommission(data)}
              />
            </span>
          </div>
        </div>
        {Loadable.hasValue(funding) && fundingMode === 'missingAmount' && funding.missingAmount > 0 && (
          <div className={classes.column}>
            <div className={classes.row}>
              <span
                data-testid="order-currency-exchange-summary-available-balance-label"
                className={classes.label}
              >
                {t.availableBalance}
              </span>
              <MoneyMaskHOC
                isInverse
                size={100}
                bypass={sourceComponent === OrderFormSourceComponent.Modal}
              >
                <NbIndefiniteLoaderValueWrapper
                  loadableValue={funding}
                  loadedValueCb={data => (
                    <span
                      data-testid="order-currency-exchange-summary-available-balance-value"
                      className={classes.value}
                    >
                      {getAvailableBalance(data)}
                    </span>
                  )}
                />
              </MoneyMaskHOC>
            </div>
            <div className={classes.row}>
              <span
                data-testid="order-currency-exchange-summary-missing-amount-label"
                className={classes.label}
              >
                {t.missingAmount}
              </span>
              <MoneyMaskHOC
                isInverse
                size={100}
                bypass={sourceComponent === OrderFormSourceComponent.Modal}
              >
                <NbIndefiniteLoaderValueWrapper
                  loadableValue={funding}
                  loadedValueCb={data => (
                    <span
                      data-testid="order-currency-exchange-summary-missing-amount-value"
                      className={classes.value}
                    >
                      {getMissingValue(data)}
                    </span>
                  )}
                />
              </MoneyMaskHOC>
            </div>
          </div>
        )}
      </div>
      <div className={classes.divider} />
      <div className={classes.section}>
        <div className={classes.column}>
          <div className={classes.row}>
            <span
              data-testid="order-currency-exchange-summary-exchange-rate-label"
              className={classes.label}
            >
              {t.exchangeRate}
            </span>
            <span
              data-testid="order-currency-exchange-summary-exchange-rate-value"
              className={classes.value}
            >
              <NbIndefiniteLoaderValueWrapper
                loaderProps={{ height: 20 }}
                loadableValue={fundingWithOrderFields}
                loadedValueCb={data => getExchangeRateLabel(data)}
              />
            </span>
          </div>
        </div>
        <div className={classes.column}>
          <div className={classes.row}>
            <span
              data-testid="order-currency-exchange-summary-exchange-amount-label"
              className={classes.label}
            >
              {t.exchangeAmount}
            </span>
            <span
              data-testid="order-currency-exchange-summary-exchange-amount-value"
              className={classes.value}
            >
              <NbIndefiniteLoaderValueWrapper
                loaderProps={{ height: 20 }}
                loadableValue={fundingWithOrderFields}
                loadedValueCb={data => getExchangeAmount(data)}
              />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
